'use client';

import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  use,
  useMemo,
  useState,
} from 'react';
import { DefaultError, useQuery } from '@tanstack/react-query';

import { useSelectedStore } from '@/hooks';
import {
  FulfillmentType,
  PromoCarouselDocumentDataCarouselItemsItemWithID,
  TreezSpecials,
} from '@/types';
import { getActivesPreCartDiscounts, getPreCartTreezDiscounts } from '@/utils';
import { useData } from '@/components';
import { getPromoCarouselItems } from '@/components/PromoCard/utils';
import { useDiscountService } from 'data/sell-treez/services/discount';
import { useFulFillmentType } from '@/hooks/useFulFillmentType';

interface Specials {
  error: DefaultError | null;
  isError: boolean;
  isLoading: boolean;
  specials: TreezSpecials[];
  preCartTreezDiscounts: TreezSpecials[];
  activesPreCartDiscounts: Record<
    string,
    PromoCarouselDocumentDataCarouselItemsItemWithID
  >;
  removeBUYProductBundleId?: string | null;
  setRemoveBUYProductBundleId: Dispatch<
    SetStateAction<string | null | undefined>
  >;
}

const SpecialsContext = createContext<Specials>({
  error: null,
  isError: false,
  isLoading: false,
  specials: [],
  preCartTreezDiscounts: [],
  activesPreCartDiscounts: {},
  removeBUYProductBundleId: null,
  setRemoveBUYProductBundleId: () => {},
});
SpecialsContext.displayName = 'SpecialsContext';

interface SpecialsProviderProps extends PropsWithChildren {
  fulfillmentType?: FulfillmentType;
}

const SpecialsProvider: FC<SpecialsProviderProps> = ({
  children,
  fulfillmentType: fulfillmentTypeProp,
}) => {
  const { store } = useSelectedStore();
  const { promoCarousel } = useData();

  const { fulfillmentType: fulfillmentTypeFromHook } = useFulFillmentType();
  const fulfillmentType = fulfillmentTypeProp || fulfillmentTypeFromHook;
  const { getDiscounts } = useDiscountService({ fulfillmentType });

  const [removeBUYProductBundleId, setRemoveBUYProductBundleId] =
    useState(null);

  const { data, isLoading, error, isError } = useQuery({
    queryKey: ['specials', store?.short_name, fulfillmentType],
    queryFn: () => getDiscounts(),
    enabled: !!store?.mso_store_entity_id, // dont run the query if the store entity id is not set
  });

  const specials = data ?? [];

  // Calculate preCartTreezDiscounts and activesPreCartDiscounts
  const { preCartTreezDiscounts, activesPreCartDiscounts } = useMemo(() => {
    const preCartDiscounts = getPreCartTreezDiscounts(specials);
    const cmsDiscountsItems = getPromoCarouselItems(promoCarousel);
    const activesDiscounts = getActivesPreCartDiscounts(
      cmsDiscountsItems,
      preCartDiscounts,
    );

    return {
      preCartTreezDiscounts: preCartDiscounts,
      activesPreCartDiscounts: activesDiscounts,
    };
  }, [specials, promoCarousel]);

  return (
    <SpecialsContext
      value={{
        isLoading,
        specials,
        preCartTreezDiscounts,
        activesPreCartDiscounts,
        error,
        isError,
        removeBUYProductBundleId,
        setRemoveBUYProductBundleId,
      }}
    >
      {children}
    </SpecialsContext>
  );
};

const useSpecialsContext = () => {
  const specialsContext = use(SpecialsContext);

  return specialsContext;
};

export { SpecialsProvider, useSpecialsContext };
